import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ChangepassService } from './changepass.service';

@Component({
  selector: 'app-changepass',
  templateUrl: './changepass.component.html',
  styleUrls: ['./changepass.component.scss']
})
export class ChangepassComponent implements OnInit {
  @Input() id: number;
  @Input() clientSetting: any;
  @Output() fadeOutEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();

  getClientsSettingAppAuth: any;

  public mismatch: boolean = false;
  public user: any;
  public message: string;
  public messageChangePass: boolean = false;
  public formChangePass: boolean = true;
  public changePassUpdated: boolean = false;

  showPassword: boolean = false;
  showPasswordConfirm: boolean = false;

  changePassForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private changePassService: ChangepassService,
  ) { }

ngOnInit(): void {
    this.getClientsSettingAppAuth = this.clientSetting;
    this.changePassForm = this.formBuilder.group({
        password: new FormControl('', [
            Validators.required,
            Validators.minLength(12),  // ✅ Mínimo 12 caracteres
            this.passwordComplexityValidator // ✅ Validador personalizado
        ]),
        confirmPassword: new FormControl('', [Validators.required])
    }, {
        validators: this.passwordMatchValidator // ✅ Validador de coincidencia a nivel de grupo
    });
}

// ✅ Validador de complejidad
passwordComplexityValidator(control: FormControl): { [key: string]: boolean } | null {
    const password = control.value;

    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isValidLength = password.length >= 12;

    const isValid = hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength;

    if (!isValid) {
        return { passwordWeak: true };
    }

    return null;
}

// ✅ Validador de coincidencia de contraseñas
passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    if (password && confirmPassword && password !== confirmPassword) {
        return { passwordMismatch: true };
    }

    return null;
}


// ✅ Genera una contraseña aleatoria que cumple con passwordComplexityValidator
generateSecurePassword(length: number = 16): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // sin I/O para evitar confusión visual
  const lower = 'abcdefghijkmnpqrstuvwxyz'; // sin l/o
  const numbers = '23456789'; // sin 0/1
  const special = '!@#$%^&*(),.?":{}|<>'; // mismo set que passwordComplexityValidator
  const all = upper + lower + numbers + special;

  const randomChar = (charset: string): string => {
    const randomValues = new Uint32Array(1);
    window.crypto.getRandomValues(randomValues);
    return charset[randomValues[0] % charset.length];
  };

  // Garantiza al menos un carácter de cada categoría exigida por el validador
  const passwordChars = [
    randomChar(upper),
    randomChar(lower),
    randomChar(numbers),
    randomChar(special),
    ...Array.from({ length: length - 4 }, () => randomChar(all))
  ];

  // Fisher-Yates con crypto, para no dejar siempre los 4 obligatorios al principio
  for (let i = passwordChars.length - 1; i > 0; i--) {
    const randomValues = new Uint32Array(1);
    window.crypto.getRandomValues(randomValues);
    const j = randomValues[0] % (i + 1);
    [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
  }

  return passwordChars.join('');
}

onGeneratePassword(): void {
  const generated = this.generateSecurePassword();

  this.changePassForm.patchValue({
    password: generated,
    confirmPassword: generated
  });

  this.changePassForm.get('password')?.markAsTouched();
  this.changePassForm.get('password')?.markAsDirty();
  this.changePassForm.get('confirmPassword')?.markAsTouched();
  this.changePassForm.get('confirmPassword')?.markAsDirty();

  this.mismatch = false;
  this.showPassword = false;
  this.showPasswordConfirm = false;
}

togglePasswordVisibility() {
  this.showPassword = !this.showPassword;
}

togglePasswordConfirmVisibility() {
  this.showPasswordConfirm = !this.showPasswordConfirm;
}


  onClick() {
    const button = document.querySelector('.btn-send');
    if (button) {
      button.classList.add('clicked');
      setTimeout(() => {
        button.classList.remove('clicked');
      }, 1000);
    }
  }

  // Calcular fortaleza de contraseña
getPasswordStrength(): string {
    const password = this.changePassForm.get('password')?.value;
    if (!password) return '';

    let strength = 0;

    if (password.length >= 12) strength++;
    if (password.length >= 16) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    if (strength <= 5) return 'strong';
    return 'very-strong';
}

getStrengthText(): string {
    const strength = this.getPasswordStrength();
    switch(strength) {
        case 'weak': return 'Débil';
        case 'medium': return 'Media';
        case 'strong': return 'Fuerte';
        case 'very-strong': return 'Muy fuerte';
        default: return '';
    }
}

  onSubmit() {
    if (this.changePassForm.valid) {
      const password = this.changePassForm.get('password')?.value;
      const confirmPassword = this.changePassForm.get('confirmPassword')?.value;

      if (password === confirmPassword) {
        // Las contraseñas coinciden, procede con el envío del formulario
        const payload = {
          id: this.id,
          password_new: this.changePassForm.value.password,
          client_id: this.getClientsSettingAppAuth.id
        }

        this.flagEventOpen();

        this.changePassService.resetPassword(payload).subscribe((res: any) => {
          if(res.cod === 200) {
            this.flagEventClose(true, res.det);
          } else {
            this.flagEventClose(false)
          }
        });
      } else {
        // Las contraseñas no coinciden, muestra el mensaje de error
        this.mismatch = true;
      }
    }
  }

  closeChangePass() {
    this.closeModal.emit();
  }

  private flagEventOpen() {
    this.formChangePass = false;
    this.changePassUpdated = true;
  }

  private flagEventClose(status: boolean, message?: string) {
    if(status) {
      this.flagEvent();
      this.message = message;
    } else {
      this.flagEvent();
      this.message = 'Ha ocurrido un error, vuelva a intentarlo en un momento';
    }
    this.fadeOutEvent.emit();
  }

  private flagEvent() {
    this.changePassUpdated = false;
    this.messageChangePass = true;
  }


}
